import clsx from 'clsx'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { ReactComponent as CaretDown } from 'assets/icons/caret-down.svg'
import Check from 'assets/icons/check.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search-icon.svg'

import { Divider } from './Divider'
import { Icon } from './Icon'
import { Tooltip } from './Tooltip'

interface Option {
  value: any
  label: string
  disabled?: boolean
}

interface MultiSelectChangeEvent {
  detail: { value: any[] }
  target: { value: any[] }
}

interface MultiSelectProps {
  value?: any[]
  onChange?: (event: MultiSelectChangeEvent) => void
  children: React.ReactNode
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: boolean
  helpText?: string
  labelConfig?: { text?: string }
  tooltipText?: string
  className?: string
  id?: string
  hideSearch?: boolean
}

const extractOptions = (children: React.ReactNode): Option[] =>
  React.Children.toArray(children).map((child: any) => {
    const val = child.props.value
    let label = child.props.children
    if (React.isValidElement(label)) {
      const props = label.props as { slot?: string; children?: React.ReactNode }
      if (props.slot === 'label') label = props.children
    }
    return { value: val, label: String(label), disabled: child.props.disabled }
  })

const LABEL_RIGHT_GAP = 8

// Renders an option label that truncates when it overflows, and only shows a
// tooltip (with the full text) when it is actually truncated.
const OptionLabel: React.FC<{ label: string; isChecked: boolean }> = ({ label, isChecked }) => {
  const spanRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = spanRef.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  }, [label])

  return (
    <Tooltip content={label} hide={!isTruncated} className="min-w-0 flex-1">
      <span ref={spanRef} className={clsx('block truncate', isChecked && 'text-black')}>
        {label}
      </span>
    </Tooltip>
  )
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  value,
  onChange,
  children,
  placeholder = 'Select',
  disabled = false,
  required = false,
  error = false,
  helpText,
  labelConfig,
  tooltipText,
  className,
  hideSearch = true,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const controlsRef = useRef<HTMLDivElement>(null)

  const options = extractOptions(children)
  const selectedValues: any[] = Array.isArray(value) ? value : []
  const selectedOptions = options.filter(opt => selectedValues.includes(opt.value))
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))

  const [displayLabel, setDisplayLabel] = useState<string>(placeholder)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) setSearchQuery('')
  }, [isOpen])

  // Build the displayed label:
  // - No selection -> placeholder
  // - Every option selected -> "All selected"
  // - Otherwise try to fit as many full labels as possible, comma separated,
  //   suffixed with "+N" for the rest. If even the first label doesn't fit,
  //   fall back to "N selected". Labels are never truncated mid-word.
  useLayoutEffect(() => {
    const total = selectedOptions.length

    if (total === 0) {
      setDisplayLabel(placeholder)
      return
    }

    if (options.length > 0 && total === options.length) {
      setDisplayLabel('All selected')
      return
    }

    const measure = () => {
      const buttonEl = buttonRef.current
      const labelEl = labelRef.current

      if (!buttonEl || !labelEl) {
        setDisplayLabel(`${total} selected`)
        return
      }

      const buttonStyle = window.getComputedStyle(buttonEl)
      const paddingLeft = parseFloat(buttonStyle.paddingLeft) || 0
      const paddingRight = parseFloat(buttonStyle.paddingRight) || 0
      const controlsWidth = controlsRef.current?.offsetWidth ?? 0
      const availableWidth = buttonEl.clientWidth - paddingLeft - paddingRight - controlsWidth - LABEL_RIGHT_GAP

      const labelStyle = window.getComputedStyle(labelEl)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        setDisplayLabel(`${total} selected`)
        return
      }

      ctx.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`

      const labels = selectedOptions.map(opt => opt.label)

      for (let k = total; k >= 1; k--) {
        const candidate = k === total ? labels.join(', ') : `${labels.slice(0, k).join(', ')}, +${total - k}`
        const textWidth = ctx.measureText(candidate).width

        if (textWidth <= availableWidth) {
          setDisplayLabel(candidate)
          return
        }
      }

      setDisplayLabel(`${total} selected`)
    }

    measure()

    const resizeObserver = new ResizeObserver(measure)
    if (buttonRef.current) resizeObserver.observe(buttonRef.current)

    return () => resizeObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues.join('|'), options.length])

  const emitChange = (newValue: any[]) => {
    onChange?.({ detail: { value: newValue }, target: { value: newValue } })
  }

  const handleCheckboxToggle = (option: Option) => {
    if (option.disabled) return
    const exists = selectedValues.includes(option.value)
    const newValue = exists ? selectedValues.filter(v => v !== option.value) : [...selectedValues, option.value]
    emitChange(newValue)
  }

  return (
    <div className={clsx('relative flex flex-col gap-2', className)} ref={dropdownRef}>
      {labelConfig?.text && (
        <label htmlFor={id} className="flex items-center gap-2 text-sm leading-6 text-[#505050]">
          <span className="tracking-wide">{labelConfig.text}</span>
          {!required && <span className="text-muted-foreground">(Optional)</span>}
          {tooltipText && (
            <div className="group relative">
              {/* <Icon icon="Support" className="h-4 w-4 text-muted-foreground" /> */}
              <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-3 py-1.5 text-xs text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                {tooltipText}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-foreground" />
              </div>
            </div>
          )}
        </label>
      )}

      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        disabled={disabled}
        className={clsx(
          'w-full h-10 flex justify-between rounded-lg items-center border border-[#a2a9b0] border-solid px-[10px] py-[9px] text-sm transition-colors hover:border-[#697077] hover:!bg-[#f0f2F5]',
          error &&
            'border-[#DE0000] text-foreground focus:ring-[#DE0000] hover:border-[#DE0000] hover:text-[#000] hover:!bg-[#f0f2F5]',
          !error && !disabled && 'hover:border-black hover:text-black hover:!bg-[#f0f2F5]',
          disabled && 'cursor-not-allowed opacity-50',
          !error && 'border-foreground',
          selectedOptions.length === 0 ? 'text-[#5f5f5f] border-[#9B9B9B]' : 'text-[#191919] border-[#9B9B9B]',
        )}
      >
        <span ref={labelRef} className="text-start min-w-[40px] whitespace-nowrap overflow-hidden">
          {displayLabel}
        </span>
        <div ref={controlsRef} className="flex gap-2 items-center justify-between flex-shrink-0">
          <Icon icon={CaretDown} className={clsx('w-4 transition-transform', isOpen && '-rotate-180')} />
        </div>
      </button>

      {helpText && (
        <div className={clsx('text-xs leading-5', error ? 'text-[#DE0000]' : 'text-[#505050]')}>{helpText}</div>
      )}

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 w-full min-w-[220px] rounded-md bg-popover shadow-basic bg-white z-[3333]"
          style={{ boxShadow: '0px 1px 4px 0px rgba(53, 59, 65, 0.05), 0px 4px 16px 0px rgba(53, 59, 65, 0.12)' }}
        >
          {!hideSearch && (
            <>
              <div className="flex items-center gap-2 p-2">
                <Icon icon={SearchIcon} className="[&_path]:fill-[#505050]" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm leading-6 outline-none text-[#505050] placeholder:text-[#b1b0b0]"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-[12px] border-none bg-transparent cursor-pointer p-0"
                    onClick={() => {
                      setSearchQuery('')
                    }}
                    aria-label="Clear search"
                  >
                    <Icon icon={CloseIcon} aria-label="Clear icon" color="#8b919a" />
                  </button>
                )}
              </div>
              <Divider />
            </>
          )}
          <div className="max-h-[350px] overflow-y-auto overflow-x-hidden p-2 flex flex-col gap-1">
            {filteredOptions.map((option, index) => {
              const isChecked = selectedValues.includes(option.value)
              return (
                <label
                  key={index}
                  htmlFor={`${id ?? 'multiselect'}-checkbox-${index}`}
                  className={clsx(
                    'flex justify-between w-full cursor-pointer text-[14px] items-center px-2 py-1 rounded-6 leading-6 text-[#191919] hover:bg-[#f0f2f5] transition-colors',
                    isChecked && '!font-medium !bg-[#eee8ff]',
                    option.disabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="flex items-center gap-3 text-sm w-full">
                    <input
                      id={`${id ?? 'multiselect'}-checkbox-${index}`}
                      type="checkbox"
                      checked={isChecked}
                      disabled={option.disabled}
                      onChange={() => handleCheckboxToggle(option)}
                      style={{ backgroundImage: `url(${isChecked && Check})` }}
                      className="size-5 flex-shrink-0 rounded border border-solid border-[#6E6E6E] accent-black appearance-none checked:bg-[#8508e8] bg-center bg-no-repeat"
                    />
                    <OptionLabel label={option.label} isChecked={isChecked} />
                  </div>
                </label>
              )
            })}
            {filteredOptions.length === 0 && (
              <div className="px-6 py-4 text-center text-muted-foreground">No items found</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiSelect
